import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  AddButton,
  ThinDropdown,
  MultiSelectDropdown,
} from "../../../components/common/GeneralComponent";
import { toast } from "react-toastify";
import { fetchData, submitFormWithErrors } from "../../../utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { SDG_OPTIONS } from "../sdgOptions";

const MAX_CURRENCY_AMOUNT = 9999999999999.99;
const CURRENCY_MAX_MESSAGE = "Amount is too large (max 13 digits)";

const MicroGrantDetails = ({
  grantId,
  onNext,
  isEditMode,
  showNextOnly,
  onStoreFormData,
  onValidationChange,
  initialData,
  onGrantUpdated,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [applicationFile, setApplicationFile] = useState(null);

  const validationSchema = Yup.object({
    business_category: Yup.string().required("Business Category is required"),
    business_entity: Yup.string().required("Business entity is required"),
    business_entity_other: Yup.string().when("business_entity", {
      is: "Other/Specify",
      then: (schema) => schema.required("Please specify other business entity").matches(/^[^0-9]*$/, "Numbers are not allowed"),
      otherwise: (schema) => schema.nullable(),
    }),
    business_sector: Yup.string().required("Business sector is required"),
    business_sub_type: Yup.string().nullable(),
    business_sector_other: Yup.string().when("business_sector", {
      is: "Other/Specify",
      then: (schema) => schema.required("Please specify other business sector").matches(/^[^0-9]*$/, "Numbers are not allowed"),
      otherwise: (schema) => schema.nullable(),
    }),
    grant_asked_amount: Yup.number()
      .positive("Grant asked amount must be positive")
      .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
      .required("Grant asked amount is required"),
    cost_rationalization_amount: Yup.number()
      .positive("Cost rationalization amount must be positive")
      .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
      .required("Cost rationalization amount is required"),
    item_details: Yup.string().required("Item details is required"),
    ecib_clearance: Yup.boolean().required("ECIB clearance is required"),
    registration_date: Yup.date().required("Registration date is required"),
    application_submission_date: Yup.date().required(
      "Application submission date is required"
    ),
    selection_date: Yup.date()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable(),
    approval_date: Yup.date().required("Approval date is required"),
    disbursement_date: Yup.date().required("Disbursement date is required"),
  });

  const formik = useFormik({
    initialValues: {
      business_category: initialData?.business_category || "",
      business_entity: initialData?.business_entity || "",
      business_entity_other: initialData?.business_entity_other || "",
      business_sector: initialData?.business_sector || "",
      sdg_interventions: initialData?.sdg_interventions || [],
      business_sub_type: initialData?.business_sub_type || "",
      business_sector_other: initialData?.business_sector_other || "",
      grant_asked_amount: initialData?.grant_asked_amount || "",
      cost_rationalization_amount:
        initialData?.cost_rationalization_amount || "",
      item_details: initialData?.item_details || "",
      ecib_clearance: initialData?.ecib_clearance ?? "",
      registration_date: initialData?.registration_date || "",
      application_submission_date:
        initialData?.application_submission_date || "",
      selection_date: initialData?.selection_date || "",
      approval_date: initialData?.approval_date || "",
      disbursement_date: initialData?.disbursement_date || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await handleFormSubmit(values);
    },
  });

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      if (isEditMode) {
        const formData = new FormData();
        Object.keys(values).forEach((key) => {
          const value = values[key];
          formData.append(
            key,
            Array.isArray(value)
              ? JSON.stringify(value)
              : value === null || value === undefined
              ? ""
              : value
          );
        });

        if (applicationFile && applicationFile instanceof File) {
          formData.append("application_file", applicationFile);
        }

        const response = await fetchData(
          `micro-grants/${grantId}`,
          "PUT",
          formData,
          {},
          {},
          navigate,
          "",
          dispatch
        );

        if (response) {
          toast.success("Micro Grant details updated successfully");
          if (onGrantUpdated) {
            onGrantUpdated();
          }
        }
      } else {
        // For create mode, prepare data similar to SME grants
        const dataWithFile = {
          ...values,
        };

        // Include file information in the data
        if (applicationFile) {
          if (applicationFile instanceof File) {
            dataWithFile.application_file = applicationFile;
          } else {
            // If it's already a string path
            dataWithFile.application_file = applicationFile;
          }
        }

        if (onStoreFormData) {
          onStoreFormData(dataWithFile);
        }

        toast.success("Micro Grant details saved locally");
        if (onNext) onNext();
      }
    } catch (error) {
      console.error("Error saving micro grant details:", error);
      toast.error("Failed to save micro grant details");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const maxSize = 1 * 1024 * 1024;

      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a PDF or Word document");
        e.target.value = "";
        return;
      }

      if (file.size > maxSize) {
        toast.error("File size should be less than 1MB");
        e.target.value = "";
        return;
      }

      setApplicationFile(file);

      // Immediately update the form data with the file
      const updatedFormData = {
        ...formik.values,
        application_file: file,
      };

      if (onStoreFormData) {
        onStoreFormData(updatedFormData);
      }
    }
  };

  const handleNextClick = async () => {
    if (loading) return;

    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          Object.keys(formik.values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        );
        return;
      }

      const dataWithFile = {
        ...formik.values,
        application_file: applicationFile,
      };

      if (onStoreFormData) {
        onStoreFormData(dataWithFile);
      }

      toast.success("Micro Grant details saved");

      setTimeout(() => {
        if (onNext) {
          onNext();
        }
      }, 100);
    });
  };

  useEffect(() => {
    if (onValidationChange) {
      const timeoutId = setTimeout(() => {
        onValidationChange(formik.isValid);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [formik.isValid, onValidationChange]);

  useEffect(() => {
    if (onStoreFormData && !isEditMode) {
      const timeoutId = setTimeout(() => {
        const dataToStore = { ...formik.values };
        if (applicationFile) {
          dataToStore.application_file = applicationFile;
        }
        onStoreFormData(dataToStore);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [formik.values, applicationFile, onStoreFormData, isEditMode]);

  // Initialize application file from initial data
  useEffect(() => {
    if (initialData?.application_file) {
      if (typeof initialData.application_file === "string") {
        // For existing grants with file paths
        const fileInfo = {
          name: initialData.application_file.split("/").pop(),
          size: 0,
          type:
            initialData.application_file.split(".").pop() === "pdf"
              ? "application/pdf"
              : "application/msword",
        };
        setApplicationFile(fileInfo);
      } else if (initialData.application_file instanceof File) {
        // For File objects
        setApplicationFile(initialData.application_file);
      }
    }
  }, [initialData]);

  const businessCategoryOptions = [
    { label: "New", value: "New" },
    { label: "Existing", value: "Existing" },
    { label: "Expansion", value: "Expansion" },
  ];

  const businessEntityOptions = [
    { label: "Partnership", value: "Partnership" },
    { label: "Private Ltd", value: "Private Ltd" },
    { label: "Sole Proprietor", value: "Sole Proprietor" },
    { label: "Association", value: "Association" },
    { label: "Other/Specify", value: "Other/Specify" },
  ];

  const businessSectorOptions = [
    { label: "Aquaculture & Fisheries", value: "Aquaculture & Fisheries" },
    {
      label: "Agri-Food & Beverage Processing",
      value: "Agri-Food & Beverage Processing",
    },
    {
      label: "Livestock, Poultry & Meat Value Chain",
      value: "Livestock, Poultry & Meat Value Chain",
    },
    {
      label: "Textiles, Apparel & Handicrafts",
      value: "Textiles, Apparel & Handicrafts",
    },
    {
      label: "Automotive & Light Engineering",
      value: "Automotive & Light Engineering",
    },
    {
      label: "Construction Materials & Prefabrication",
      value: "Construction Materials & Prefabrication",
    },
    {
      label: "Renewable Energy & Energy Services",
      value: "Renewable Energy & Energy Services",
    },
    { label: "Health & Care Services", value: "Health & Care Services" },
    {
      label: "Education, Training & EdTech",
      value: "Education, Training & EdTech",
    },
    {
      label: "Tourism, Hospitality & Creative Services",
      value: "Tourism, Hospitality & Creative Services",
    },
    { label: "ICT & Digital Services", value: "ICT & Digital Services" },
    {
      label: "Waste, Recycling & Circular Economy",
      value: "Waste, Recycling & Circular Economy",
    },
    {
      label: "Retail, Distribution & Service Trades",
      value: "Retail, Distribution & Service Trades",
    },
    { label: "Other/Specify", value: "Other/Specify" },
  ];

  const handleBusinessSectorChange = (e) => {
    const value = e.target.value;
    formik.setValues((prev) => ({
      ...prev,
      business_sector: value,
      ...(value !== "Other/Specify" && { business_sub_type: "" }),
    }));
  };

  return (
    <form onSubmit={submitFormWithErrors(formik)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1">
          <ThinDropdown
            label="Business Category"
            id="business-category-dropdown"
            options={businessCategoryOptions}
            value={formik.values.business_category}
            onChange={formik.handleChange}
            name="business_category"
            error={
              formik.touched.business_category &&
              formik.errors.business_category
            }
          />
          {formik.touched.business_category &&
            formik.errors.business_category && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.business_category}
              </div>
            )}
        </div>

        <div className="md:col-span-1">
          <ThinDropdown
            label="Business Entity Type"
            id="business-entity-dropdown"
            options={businessEntityOptions}
            value={formik.values.business_entity}
            onChange={formik.handleChange}
            name="business_entity"
            error={
              formik.touched.business_entity && formik.errors.business_entity
            }
          />
          {formik.touched.business_entity && formik.errors.business_entity && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.business_entity}
            </div>
          )}
          {formik.values.business_entity === "Other/Specify" && (
            <ThinTextField
              label="Specify Other Business Entity Type"
              name="business_entity_other"
              value={formik.values.business_entity_other}
              onChange={(e) =>
                formik.setFieldValue("business_entity_other", e.target.value.replace(/[0-9]/g, ""))
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.business_entity_other &&
                formik.errors.business_entity_other
              }
              placeholder="Please specify"
            />
          )}
          {formik.values.business_entity === "Other/Specify" &&
            formik.touched.business_entity_other &&
            formik.errors.business_entity_other && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.business_entity_other}
              </div>
            )}
        </div>

        <div className="md:col-span-1">
          <ThinDropdown
            label="Business Sector"
            id="business-sector-dropdown"
            options={businessSectorOptions}
            value={formik.values.business_sector}
            onChange={handleBusinessSectorChange}
            name="business_sector"
            error={
              formik.touched.business_sector && formik.errors.business_sector
            }
          />
          {formik.touched.business_sector && formik.errors.business_sector && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.business_sector}
            </div>
          )}
          {formik.values.business_sector === "Other/Specify" && (
            <ThinTextField
              label="Specify Other Business Sector"
              name="business_sector_other"
              value={formik.values.business_sector_other}
              onChange={(e) =>
                formik.setFieldValue("business_sector_other", e.target.value.replace(/[0-9]/g, ""))
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.business_sector_other &&
                formik.errors.business_sector_other
              }
              placeholder="Please specify"
            />
          )}
          {formik.values.business_sector === "Other/Specify" &&
            formik.touched.business_sector_other &&
            formik.errors.business_sector_other && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.business_sector_other}
              </div>
            )}
        </div>

        <div className="md:col-span-1">
          <MultiSelectDropdown
            label="SDG Interventions"
            options={SDG_OPTIONS}
            selectedValues={formik.values.sdg_interventions}
            onChange={(values) =>
              formik.setFieldValue("sdg_interventions", values)
            }
            placeholder="Select SDG Interventions"
          />
        </div>

        {formik.values.business_sector !== "Other/Specify" && (
          <div className="md:col-span-1">
            <ThinTextField
              label="Business Sub Type"
              name="business_sub_type"
              isRequired={false}
              value={formik.values.business_sub_type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.business_sub_type &&
                formik.errors.business_sub_type
              }
              placeholder="Enter business sub type"
            />
            {formik.touched.business_sub_type &&
              formik.errors.business_sub_type && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.business_sub_type}
                </div>
              )}
          </div>
        )}

        <div>
          <ThinTextField
            label="Grant Asked Amount (PKR) (Million)"
            name="grant_asked_amount"
            type="number"
            step="0.000001"
            value={formik.values.grant_asked_amount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.grant_asked_amount &&
              formik.errors.grant_asked_amount
            }
            placeholder="e.g. 1.5 (million)"
          />
          {formik.touched.grant_asked_amount &&
            formik.errors.grant_asked_amount && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.grant_asked_amount}
              </div>
            )}
        </div>

        <div>
          <ThinTextField
            label="Cost Rationalization Amount (PKR) (Million)"
            name="cost_rationalization_amount"
            type="number"
            step="0.000001"
            value={formik.values.cost_rationalization_amount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.cost_rationalization_amount &&
              formik.errors.cost_rationalization_amount
            }
            placeholder="e.g. 1.5 (million)"
          />
          {formik.touched.cost_rationalization_amount &&
            formik.errors.cost_rationalization_amount && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.cost_rationalization_amount}
              </div>
            )}
        </div>

        <div>
          <ThinTextField
            label="Item Details / Equipment/Material Provided"
            name="item_details"
            value={formik.values.item_details}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.item_details && formik.errors.item_details}
            placeholder="Enter item details"
          />
          {formik.touched.item_details && formik.errors.item_details && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.item_details}
            </div>
          )}
        </div>

        <div>
          <ThinDropdown
            label="ECIB Clearance"
            id="ecib-clearance-dropdown"
            options={[
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ]}
            value={formik.values.ecib_clearance}
            onChange={(e) =>
              formik.setFieldValue("ecib_clearance", e.target.value === "true")
            }
            name="ecib_clearance"
            error={
              formik.touched.ecib_clearance && formik.errors.ecib_clearance
            }
          />
          {formik.touched.ecib_clearance && formik.errors.ecib_clearance && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.ecib_clearance}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Date of Registration for Grant"
            name="registration_date"
            type="date"
            value={formik.values.registration_date}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.registration_date &&
              formik.errors.registration_date
            }
          />
          {formik.touched.registration_date &&
            formik.errors.registration_date && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.registration_date}
              </div>
            )}
        </div>

        <div>
          <ThinTextField
            label="Date of Application Submission"
            name="application_submission_date"
            type="date"
            value={formik.values.application_submission_date}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.application_submission_date &&
              formik.errors.application_submission_date
            }
          />
          {formik.touched.application_submission_date &&
            formik.errors.application_submission_date && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.application_submission_date}
              </div>
            )}
        </div>

        <div>
          <ThinTextField
            label="Date of Selection"
            name="selection_date"
            type="date"
            value={formik.values.selection_date}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.selection_date && formik.errors.selection_date
            }
            isRequired={false}
          />
          {formik.touched.selection_date && formik.errors.selection_date && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.selection_date}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Date of Approval"
            name="approval_date"
            type="date"
            value={formik.values.approval_date}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.approval_date && formik.errors.approval_date}
          />
          {formik.touched.approval_date && formik.errors.approval_date && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.approval_date}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Date of Disbursement"
            name="disbursement_date"
            type="date"
            value={formik.values.disbursement_date}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.disbursement_date &&
              formik.errors.disbursement_date
            }
          />
          {formik.touched.disbursement_date &&
            formik.errors.disbursement_date && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.disbursement_date}
              </div>
            )}
        </div>

        <div>
          <label className="text-[0.9rem] text-[#565656] mb-1 font-[InterLight] block">
            Application File Upload
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full"
          />
          {applicationFile && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800 flex items-center">
                <span className="mr-2">✓</span>
                Selected:{" "}
                <strong className="ml-1">
                  {applicationFile instanceof File
                    ? applicationFile.name
                    : applicationFile.name || "Unknown file"}
                </strong>
              </p>
              {applicationFile instanceof File && (
                <p className="text-xs text-green-600 mt-1">
                  Size: {(applicationFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              )}
              {!(applicationFile instanceof File) &&
                typeof initialData?.application_file === "string" &&
                initialData.application_file && (
                  <a
                    href={`${process.env.REACT_APP_BASE_URL}${initialData.application_file}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 underline hover:text-blue-800 mt-1 block"
                  >
                    View / Download
                  </a>
                )}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Accepted formats: PDF, DOC, DOCX (Max size: 1MB)
          </p>
        </div>
      </div>

      <div className="pt-4 flex justify-end gap-3">
        {showNextOnly && !isEditMode && (
          <button
            type="button"
            onClick={handleNextClick}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Saving..." : "Next"}
          </button>
        )}
        {isEditMode && (
          <AddButton
            text="Update"
            type="submit"
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </form>
  );
};

export default MicroGrantDetails;